import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';

import ContactForm from '../components/forms/ContactForm';
import Modal from '../components/modals/Modal';
import ViewContactModal from '../components/modals/ViewContactModal';
import SearchBar from '../components/SearchBar';
import LeftNav from '../core/LeftNav';
import hamburger from '../core/assets/hamburger.png';
import close from '../core/assets/close.png';
import { useContactsContext } from '../contexts/ContactsContext';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import { DeviceQueries } from '../components/styled';

function Dashboard() {
  const contactsContext = useContactsContext();
  const { pathname } = useLocation();

  const [page, setPage] = useState('contacts');
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    const match = matchPath('/*', pathname);

    const param = match?.params['*'];
    if (param) {
      setPage(param);
    }
  }, [pathname]);

  useEffect(() => {
    if (contactsContext.contactToEdit && page !== 'add') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [contactsContext.contactToEdit]);

  const onEditClose = () => {
    contactsContext.setContactToEdit(undefined);
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  useEffect(() => {
    window.addEventListener('resize', () => {
      const isCurrentlyMobile = window.innerWidth < 767;
      if (!isCurrentlyMobile) {
        setShowMenu(false);
      }
    }, false);
  }, []);

  let header: ReactNode = null;
  if (page == 'contacts') {
    header = (
      <div>
        <Row as={ContentHeader} className="g-0">
          <TableHeaderFirst xs={4} md={4} xl={3}>Name</TableHeaderFirst>
          <TableHeader xs={6} md={3}>Phone / Email</TableHeader>
          <TableHeader xs={3} $hideOnMobile={true}>Other Info</TableHeader>
          <TableHeaderMobileMenu xs={2} onClick={toggleMenu}>
            <IconHamburger alt="navigation" src={hamburger}/>
          </TableHeaderMobileMenu>
        </Row>
      </div>
    );
  } else if (page == 'review') {
    header = (
      <div>
        <Row as={ContentHeader} className="g-0">
          <TableHeaderNoPadding xs={10}>Select the people that are the same</TableHeaderNoPadding>
          <TableHeaderMobileMenu xs={2} onClick={toggleMenu}>
            <IconHamburger alt="navigation" src={hamburger}/>
          </TableHeaderMobileMenu>
        </Row>
      </div>
    );
  } else if (page == 'birthday') {
    header = (
      <div>
        <Row as={ContentHeader} className="g-0">
          <TableHeaderFirst xs={4} xl={2}>Name</TableHeaderFirst>
          <TableHeader xs={6}>Birthday</TableHeader>
          <TableHeaderMobileMenu xs={2} onClick={toggleMenu}>
            <IconHamburger alt="navigation" src={hamburger}/>
          </TableHeaderMobileMenu>
        </Row>
      </div>
    );
  } else if (page == 'add') {
    header = (
      <div>
        <Row as={ContentHeader} className="g-0">
          <TableHeaderNoPadding xs={10}>Add New Contact</TableHeaderNoPadding>
          <TableHeaderMobileMenu xs={2} onClick={toggleMenu}>
            <IconHamburger alt="navigation" src={hamburger}/>
          </TableHeaderMobileMenu>
        </Row>
      </div>
    );
  } else if (page == 'support') {
    header = (
      <div>
        <Row as={ContentHeader} className="g-0">
          <TableHeaderNoPadding xs={10}>Contact Customer Support</TableHeaderNoPadding>
          <TableHeaderMobileMenu xs={2} onClick={toggleMenu}>
            <IconHamburger alt="navigation" src={hamburger}/>
          </TableHeaderMobileMenu>
        </Row>
      </div>
    );
  }

  return (
    <DashboardContainer>

      <DashboardSideNav>
        <LeftNav page={page} mobile={false}/>
      </DashboardSideNav>
      <DashboardTop>
        <SearchBar/>
        {header}
      </DashboardTop>
      {showMenu &&
        <DashboardMobileNav>
          <LeftNav page={page} toggleMenu={toggleMenu} mobile={true}/>
        </DashboardMobileNav>}
      <DashboardMainContent>
        <Outlet/>
      </DashboardMainContent>


      {contactsContext.contactToEdit && page !== 'add' && (
        <Modal
          open={true}
          onClose={onEditClose}
          modalEdit={true}
          canClickOutside={false}
        >
          <EditContactHeader>Edit Contact
            <DashboardClose src={close} alt="close" onClick={onEditClose}/>
          </EditContactHeader>
          <ContactForm onSubmit={contactsContext.onEditComplete} popup={true}/>
        </Modal>
      )}
      <ViewContactModal open={!!contactsContext.selectedContact}/>


    </DashboardContainer>
  );
}

const DashboardContainer = styled.div`
  display: grid;
  grid-template-rows: 113px auto;
  grid-template-columns: 280px auto;
  height: 100vh;

  @media (${DeviceQueries.laptop}) {
    grid-template-columns: 200px auto;
  }

  @media (${DeviceQueries.mobile}) {
    grid-template-columns: auto;
  }
`;

const ContentHeader = styled.div`
  border-bottom: solid 1px #009bdf;
  padding-top: 5px;
  padding-bottom: 5px;
  background-color: white;
`;

const EditContactHeader = styled.h2`
  font-size: 24px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 21px;
`;

const TableHeader = styled(Col)`
  font-weight: 500;
  line-height: 1.43;
  font-size: 14px;
  display: inline-block;
  padding-left: 12px;
  padding-right: 12px;

  @media (${DeviceQueries.mobile}) {
    display: ${(props: { $hideOnMobile?: boolean }) => props.$hideOnMobile ? 'none' : undefined}
  }
`;

const TableHeaderNoPadding = styled(TableHeader)`
  padding-left: 0;
`;

const TableHeaderFirst = styled(TableHeader)`
  padding-left: 64px;
`;

const TableHeaderMobileMenu = styled(Col)`
  display: none;

  @media (${DeviceQueries.mobile}) {
    display: inline-block;
    text-align: right;
    padding-right: 12px;
  }
`;

const DashboardTop = styled.div`
  background-color: white;
  padding-left: 24px;
  padding-right: 24px;
  max-width: 1200px;

  @media (${DeviceQueries.mobile}) {
    left: auto;
    margin-right: 0;
    width: 100%;
  }
`;

const DashboardSideNav = styled.div`
  grid-row: span 2;
  overflow-x: hidden;
  background-color: white;
  padding-left: 12px;
  padding-right: 12px;

  @media (${DeviceQueries.mobile}) {
    display: none;
  }
`;

const DashboardMobileNav = styled.div`
  width: 100%;
  z-index: 2000;
  color: black;
  padding-left: 24px;
  padding-right: 24px;
  background-color: white;
`;

const DashboardMainContent = styled.div`
  overflow: hidden;
`;

const DashboardClose = styled.img`
  max-width: 30px;
  max-height: 30px;
  cursor: pointer;
  margin-left: 388px;
  margin-bottom: 5px;
`;

const IconHamburger = styled.img`
  max-width: 20px;
  max-height: 20px;
  cursor: pointer;
`;

export default Dashboard;
